/**
 * LA PREGUNTA AL TRABAJADOR: «Hace 5 días se avisó de MOSCA BLANCA en esta línea.
 * ¿Es lo mismo que ves ahora?»
 *
 * Sale en lugar del resumen, una sola vez, cuando en la línea elegida ya se avisó
 * de algo. Con la última FOTO de ese sitio, porque reconocer una foto es más
 * fácil que reconocer un nombre; y SIN la palabra «foco», que es jerga del mapa
 * (lo vigila `focoTextos.parity.test.ts` en los nueve idiomas).
 *
 * Si en la línea hay VARIOS sitios avisados (otra plaga, o la misma a más de
 * 30 m), se enseñan todos con su foto y elige él: «¿es alguno de estos?» (D24).
 *
 * Botones grandes y una sola decisión por pantalla: quien contesta tiene guantes,
 * sol en la pantalla y prisa. Lo que conteste es una PISTA para quien confirma;
 * aquí no se enlaza nada.
 */
import { useTranslation } from "react-i18next";
import type { FollowUpAnswer } from "@/lib/health/focusLinking";
import { seenKey, type FollowUpCandidate, type FollowUpVerdict } from "@/lib/pests/followUp";
import { VerdictButtons } from "@/components/worker/VerdictButtons";
import { ImgOffline } from "@/components/offline/ImgOffline";

const OPTION =
  "rounded-2xl bg-white border border-[#b6dded] shadow-sm p-4 flex items-center justify-center text-base font-semibold active:scale-[0.98] transition-transform min-h-[64px]";

const ANSWERS: { id: FollowUpAnswer; key: string }[] = [
  { id: "same", key: "yes" },
  { id: "different", key: "no" },
  { id: "unknown", key: "unknown" },
];

export function FollowUpStep({
  candidate,
  candidates,
  stage,
  fromTask = false,
  onAnswer,
  onVerdict,
  onNotHere,
}: {
  candidate: FollowUpCandidate;
  /** Todos los sitios avisados en la línea; con más de uno, se elige entre fotos. */
  candidates?: FollowUpCandidate[];
  stage: "ask_same" | "ask_how";
  fromTask?: boolean;
  /** `chosen` viene cuando eligió entre varios. */
  onAnswer: (answer: FollowUpAnswer, chosen?: FollowUpCandidate) => void;
  onVerdict: (verdict: FollowUpVerdict) => void;
  /** Desde una tarea de seguimiento: «no estoy en ese sitio». Suelta el sitio y la tarea. */
  onNotHere?: () => void;
}) {
  const { t } = useTranslation();
  const pest = candidate.pestName.toLocaleUpperCase();
  const varios = !fromTask && stage === "ask_same" && (candidates?.length ?? 0) > 1;
  const optionLabel = (c: FollowUpCandidate) =>
    (c.lineGap ?? 0) > 0
      ? t("worker.incident.followup.opt_near", { pest: c.pestName.toLocaleUpperCase(), line: c.lineNumber ?? "?" })
      : t(`worker.incident.followup.opt_${seenKey(c.daysAgo)}`, { days: c.daysAgo, pest: c.pestName.toLocaleUpperCase() });
  const cerca = (candidate.lineGap ?? 0) > 0;

  return (
    <div className="space-y-4" data-testid="followup-step" data-stage={stage} data-focus-id={candidate.focusId ?? ""} data-session-id={candidate.sessionId ?? ""} data-count={varios ? candidates!.length : 1}>
      {varios ? (
        <div className="space-y-2">
          <h2 className="text-lg font-bold text-foreground">{t("worker.incident.followup.whichOne")}</h2>
          <div className="grid grid-cols-1 gap-2">
            {candidates!.map((c) => (
              <button
                key={c.focusId ?? c.sessionId ?? c.lastDate}
                type="button"
                onClick={() => onAnswer("same", c)}
                className="flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50/70 p-2 text-left active:scale-[0.98] transition-transform"
                data-testid="followup-option"
                data-focus-id={c.focusId ?? ""}
              >
                {c.photoUrl ? (
                  <ImgOffline src={c.photoUrl} alt="" className="h-20 w-24 shrink-0 rounded-xl object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="h-20 w-24 shrink-0 rounded-xl bg-amber-100" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 text-base font-semibold text-foreground">{optionLabel(c)}</span>
              </button>
            ))}
            {ANSWERS.filter((a) => a.id !== "same").map((a) => (
              <button key={a.id} type="button" onClick={() => onAnswer(a.id)} className={OPTION} data-testid={`followup-answer-${a.id}`}>
                {t(`worker.incident.followup.${a.key}`)}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-amber-300 bg-amber-50/70 p-4 space-y-3">
            {candidate.photoUrl && (
              <ImgOffline
                src={candidate.photoUrl}
                alt=""
                className="h-40 w-full rounded-xl object-cover"
                data-testid="followup-photo"
                // Una foto que no carga no puede parar la captura: se quita y se sigue.
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <p className="text-base font-semibold text-foreground" data-testid="followup-seen">
              {fromTask
                ? t("worker.incident.followup.fromTask", { pest })
                : candidate.focusId
                  ? cerca
                    ? t(`worker.incident.followup.near_${seenKey(candidate.daysAgo)}`, { days: candidate.daysAgo, pest, line: candidate.lineNumber ?? "?" })
                    : t(`worker.incident.followup.${seenKey(candidate.daysAgo)}`, { days: candidate.daysAgo, pest })
                  : t(`worker.incident.followup.pending_${seenKey(candidate.daysAgo)}`, { days: candidate.daysAgo })}
            </p>
          </div>

          {stage === "ask_same" ? (
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-foreground">{t("worker.incident.followup.question")}</h2>
              <div className="grid grid-cols-1 gap-2">
                {ANSWERS.map((a) => (
                  <button key={a.id} type="button" onClick={() => onAnswer(a.id)} className={OPTION} data-testid={`followup-answer-${a.id}`}>
                    {t(`worker.incident.followup.${a.key}`)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-foreground">{t("worker.incident.followup.howNow")}</h2>
              <VerdictButtons onPick={onVerdict} />
              {fromTask && onNotHere && (
                <button type="button" onClick={onNotHere} className="w-full pt-2 text-center text-sm font-semibold text-muted-foreground underline" data-testid="followup-not-here">
                  {t("worker.incident.followup.notHere")}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
